import React, {Component} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {format} from 'ki-common/utils/displayFormatter';
import options from 'ki-common/options';
import {AbsTable} from '@moodysanalytics/cs-structured-ux-common';
import {ParsedErrorMessage, EditableTableCell} from './TableComponents';
import {buildCalculation} from 'ki-common/utils/explorerUtils';
import styles from '../../debt.theme.scss';
import classNames from 'classnames';

export class DebtResultsTable extends Component {
	static propTypes = {
		error: PropTypes.object,
		dataRows: PropTypes.array,
		dataColumns: PropTypes.array,
		settings: PropTypes.object,
		isLoading: PropTypes.bool,
		searchTerm: PropTypes.string,
		drillDownFundingVehicle: PropTypes.func.isRequired,
		onSortChange: PropTypes.func.isRequired,
		updateManulaInputDebtData: PropTypes.func.isRequired,
		allTemplates: PropTypes.array,
		tableDataDirty: PropTypes.bool,
	};

	static defaultProps = {
		dataRows: [],
		dataColumns: [],
		settings: {},
		isLoading: false,
		searchTerm: '',
	};

	state = {
		originalData: [],
	};

	componentDidUpdate() {
		if (this.props.dataRows !== this.state.originalData && !this.props.tableDataDirty) {
			this.setState({originalData: this.props.dataRows});
		}
	}

	getFetchedValue(rowIndex, columnName) {
		const columnIndex = this.props.dataColumns.findIndex(col => col.columnName === columnName);
		if (this.state.originalData.length > rowIndex && columnIndex >= 0) {
			return this.state.originalData[rowIndex].data[columnIndex];
		} else {
			return '0';
		}
	}

	filterRowsBySearch = row => {
		if (!this.props.searchTerm.length) {
			return true;
		}
		const textToMatch = this.props.searchTerm.toUpperCase();
		const isMatched = row.data.filter(cell => {
			return cell.toUpperCase().indexOf(textToMatch) !== -1;
		});
		return isMatched.length;
	};

	sortSelected = col => {
		let order = 'asc';
		let id = col.id || col._id;
		let calculation = col.calculation;
		if (col.sortOrder) {
			if (col.sortOrder === 'asc') {
				order = 'desc';
			} else {
				id = '';
				order = '';
				calculation = '';
			}
		}
		return this.props.onSortChange(id, order, calculation);
	};

	render() {
		const {error, dataRows = [], dataColumns = [], settings = {}, isLoading, drillDownFundingVehicle} = this.props;

		/*
		const dataColumnsAvailable = dataColumns.filter(col => {
			return col;
		});

		if (dataColumnsAvailable.length === 0) {
			return <div />;
		}
		*/

		if (error) {
			return (
				<ParsedErrorMessage
					stackTrace={_.get(error, 'data.error.error')}
					message={error.message}
					feRequestBody={_.get(error, 'data.requestBody')}
					feRequestURI={`${document.baseURI}web/debt/explore`}
					feRequestMethod="POST"
					beRequestBody={_.get(error, 'data.error.options.json')}
					beRequestURI={_.get(error, 'data.error.options.uri')}
					beRequestMethod="POST"
				/>
			);
		}

		const data = dataRows.filter(this.filterRowsBySearch);

		return (
			<AbsTable
				isSortEnabled={true}
				isFilterEnabled={true}
				isPaginationEnabled={true}
				isLoading={isLoading}
				data={data}
				noDataMessage={'No results found'}
				showNoDataMessage={!data.length}
				columns={dataColumns.map((col, idx) => {
					return {
						...col,
						// show: !(idx === 0 && settings.cohortColumn === 'debt' && !settings.fundingVehicle),
						Header: () => {
							return (
								<div className={classNames({[styles.twoLineHeader]: !!col.calculation})}>
									<div title={col.displayName}>{col.displayName}</div>
									<div>{buildCalculation(col.calculation)}</div>
								</div>
							);
						},
						id: `${col.id}-${idx}`,
						accessor: d => d.data[idx],
						Cell: cell => {
							if (col.displayName === 'Funding Vehicle Name') {
								if (settings.cohortColumn !== 'debt') {
									return (
										<div
											onClick={() => drillDownFundingVehicle(cell.value)}
											style={{
												textDecoration: 'underline',
												cursor: 'pointer',
											}}
										>
											{format(cell.value, col.displayFormat)}
										</div>
									);
								}
								return format(cell.value, col.displayFormat);
							}

							const targetTemplate = this.props.allTemplates.find(t => t._id === col._id);
							const targetAccount = this.props.allTemplates
								.filter(t => t.beginningBalanceCol && t.endingBalanceCol)
								.find(t => t._id === col.accountId);
							const targetManual = this.props.allTemplates.find(t => t._id === col._id && !t.isAccount);
							let inputPermitted = true;
							if (targetAccount) {
								inputPermitted =
									targetAccount.isActive !== false &&
									!_.has(targetTemplate, 'sourceId') &&
									(targetAccount.fundingVehicles[0] === 'all' ||
										targetAccount.fundingVehicles.includes(
											this.props.settings.quickFilters.fundingVehicleId
										));
							}
							if (targetManual && targetManual.fundingVehicles) {
								inputPermitted =
									targetManual.fundingVehicles[0] === 'all' ||
									targetManual.fundingVehicles.includes(
										this.props.settings.quickFilters.fundingVehicleId
									);
							}
							if (
								settings.viewType === 'activity' &&
								!_.has(targetTemplate, 'sourceId') &&
								(col.columnType || '').match(/^debtInput(FV|Tranche|CreditSupport|Fee|Trigger)$/) &&
								(targetTemplate &&
									!targetTemplate.isAccountBalance &&
									targetTemplate.isActive !== false) &&
								inputPermitted
							) {
								return (
									<EditableTableCell
										columnName={col.columnName}
										index={cell.row.index}
										data={cell.value}
										updateManulaInputDebtData={this.props.updateManulaInputDebtData}
										tableDataDirty={this.props.tableDataDirty}
										dataType={col.dataType}
										fetchedDataValue={
											this.props.tableDataDirty
												? this.getFetchedValue(cell.row.index, col.columnName)
												: cell.value
										}
									/>
								);
							}

							if (col.columnType === 'debt' && col.columnName === 'accrualMethod') {
								const optionMatch = options.accrualMethodOptions.find(
									({value}) => value === cell.value
								);
								const label = (optionMatch && optionMatch.label) || cell.value;
								return {label};
							}
							if (col.dataType === 'numeric') {
								return format(parseFloat(cell.value), col.displayFormat);
							}

							return format(cell.value, col.displayFormat);
						},
					};
				})}
			/>
		);
	}
}

export default DebtResultsTable;
